// Single source of truth for the 5 "Browse by Challenge" entries.
// Used by /challenges (landing), /challenges/:slug (detail), and the homepage
// "Parenting Challenges" section.
import { Brain, Moon, Monitor, Users, Heart } from 'lucide-react';

export const CHALLENGES = [
  {
    slug: 'big-feelings-meltdowns',
    title: 'Big Feelings & Meltdowns',
    short: 'When your child can\'t regulate yet — and you\'re running out of strategies.',
    headline: 'My child has frequent meltdowns.',
    intro:
      'You\'re not failing. The part of your child\'s brain that manages emotions is still being built — and that\'s why even small frustrations can spill into big storms. These toolkits give you scripts to use mid-meltdown, calm-down routines you can stick on the fridge, and the upstream habits that prevent the next one.',
    bullets: [
      'Validate feelings without giving in to behavior',
      'Stay calm yourself when your child can\'t',
      'Teach long-term emotional regulation skills',
    ],
    icon: Brain,
    productSlugs: ['emotional-regulation-toolkit', 'tantrum-meltdown-control-guide'],
    categorySlug: 'behavior-emotions',
  },
  {
    slug: 'sleep-bedtime',
    title: 'Sleep & Bedtime',
    short: 'Bedtime battles, night fears, and the lost sleep that comes with them.',
    headline: 'Bedtime takes hours, every night.',
    intro:
      'A predictable bedtime routine isn\'t about strict rules — it\'s about giving your child\'s nervous system a clear runway from busy day to safe sleep. These resources help you build a calmer wind-down, address night fears with empathy, and protect everyone\'s sleep without power struggles.',
    bullets: [
      'A wind-down routine that actually winds them down',
      'Scripts for night fears and bedtime anxiety',
      'How to protect sleep without becoming the bedtime police',
    ],
    icon: Moon,
    productSlugs: ['bedtime-routine-toolkit', 'sleep-anxiety-night-fears-guide'],
    categorySlug: 'sleep-bedtime',
  },
  {
    slug: 'screen-time',
    title: 'Screen Time',
    short: 'Tantrums when screens turn off — and the guilt about how much you give in.',
    headline: 'Screens are taking over family life.',
    intro:
      'Screen time isn\'t a moral failure — it\'s the modern parenting environment. The goal isn\'t zero, it\'s healthy. These guides help you set clear limits, recover the off-screen rituals your kids actually love, and end the daily battle over devices.',
    bullets: [
      'Set boundaries kids can predict (and accept)',
      'Replace screen time with rituals they\'ll choose',
      'Reduce daily negotiation about devices',
    ],
    icon: Monitor,
    productSlugs: ['screen-time-balance-toolkit', 'reclaim-family-time-from-screens'],
    categorySlug: 'screen-time-technology',
  },
  {
    slug: 'family-relationships',
    title: 'Family Relationships',
    short: 'When connection feels thin — between siblings, parent, child.',
    headline: 'We feel disconnected as a family.',
    intro:
      'Connection is the parenting nutrient most families are quietly short on. These toolkits give you small, ritualized ways to fill the connection cup before the day demands withdrawals — plus practical scripts for sibling conflict and tough conversations.',
    bullets: [
      'Ten-minute daily connection rituals',
      'Sibling conflict scripts that don\'t take sides',
      'Family meetings that kids actually like',
    ],
    icon: Users,
    productSlugs: ['stronger-family-connection-toolkit', 'sibling-conflict-rivalry-guide'],
    categorySlug: 'family-bonding',
  },
  {
    slug: 'confidence-focus',
    title: 'Confidence & Focus',
    short: 'Big feelings about school, focus, and trying new things.',
    headline: 'My child gives up too easily — or can\'t seem to focus.',
    intro:
      'Confidence and focus aren\'t personality traits — they\'re skills, built one small win at a time. These toolkits help you praise effort over outcome, build a real focus profile for how your child concentrates, and grow their belief that mistakes are how brains learn.',
    bullets: [
      'Praise that builds real confidence (not fragile self-esteem)',
      'A Focus Profile that identifies how your child concentrates best',
      'Help kids try again after they fail — with language that lands',
    ],
    icon: Heart,
    productSlugs: ['confidence-self-esteem-toolkit', 'focus-listening-skills-builder'],
    categorySlug: 'child-development',
  },
];

export const getChallengeBySlug = (slug) => CHALLENGES.find((c) => c.slug === slug);
